import { MyleTableColumn } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { MedicalSummaryMedicationDetailsSortColumn } from "@/Common/Models/Patient/PatientEnums";
import { SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { MedicationNote } from "@/Patient/Models/MedicalSummary/MedicationNote";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientMedicationGridHandler extends BaseOperationHandler {
    private helper: MyleHelper = new MyleHelper();

    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.MedicationGrid.SettingsToggle, this.settingsToggle);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.SettingsCancel, this.cancelSettingsToggle);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.SettingsOk, this.okSettingsToggle);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.ColumnsSettingOpen, this.columnOrderPopupOpen);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.ColumnsSettingClose, this.columnOrderPopupClose);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.SearchTextChanged, this.searchTextChanged);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.SearchDateFromChanged, this.searchDateFromChanged);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.SearchDateToChanged, this.searchDateToChanged);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.SearchStatusChanged, this.statusCheckChanged);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.SearchOriginChanged, this.statusOriginChanged);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.SearchDetailsChange, this.detailsChanged);
        this.RegisterOperation(LocalOperations.Misc.Table_ColumnSortClick, this.setColumnSort);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.RefreshMedications, this.refreshMedications);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.InsertMedicationNote, this.insertMedicationNote);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.UpdatePatientComplianceValue, this.updatePatientComplianceValue);
        this.RegisterOperation(LocalPatientOperations.MedicationGrid.LoadMedicationNotes, this.loadMedicationNotes);

        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.onContainerClick, false);
    }

    private refreshMedications(request: LocalEvent, store: PatientStore): void {
        store.ActionsMedicationTab.ReloadMedications(request.PatientId);
        this.applySortingAndOrdering(request.PatientId, store);
    }

    private settingsToggle(request: LocalEvent, store: PatientStore): void {
        let summaryModel = store.PatientMedicalSummaryModels[request.PatientId];
        let medicationGridModel = store.PatientMedicationGridModels[request.PatientId];
        let searchSettingsOpen = !medicationGridModel.get("IsSearchSettingsOpen");
        medicationGridModel = medicationGridModel.set("IsSearchSettingsOpen", searchSettingsOpen);
        store.PatientMedicationGridModels[request.PatientId] = medicationGridModel;

        let filteredMedication = summaryModel.get("FilteredMedicalItemsMedicationAndPrescription");
        if (filteredMedication.size === 0) { this.applySortingAndOrdering(request.PatientId, store); }

        store.PatientMedicationGridModels[request.PatientId] = medicationGridModel;

        if (medicationGridModel.get("IsSearchSettingsOpen")) {
            if (filteredMedication.size === 0)
                this.applySortingAndOrdering(request.PatientId, store);
        }
        else {
            window.setTimeout(
                () => {
                    var operation = new LocalEvent(LocalPatientOperations.MedicationGrid.SettingsToggle);
                    LocalEventDispatcher.dispatch(operation);
                },
                5
            );
        }

        let cloneGridModels = medicationGridModel.set("SearchText", medicationGridModel.get("SearchText"));

        // clone status checks
        let statusCheckClones = cloneGridModels.get("StatusChecks").toArray().map(x => JSON.parse(JSON.stringify(x)));
        cloneGridModels = cloneGridModels.set("StatusChecks", Immutable.List(statusCheckClones));

        // clone origin checks
        let originCheckClones = cloneGridModels.get("OriginChecks").toArray().map(x => JSON.parse(JSON.stringify(x)));
        cloneGridModels = cloneGridModels.set("OriginChecks", Immutable.List(originCheckClones));

        // clone details checks
        let detailsCheckClones = cloneGridModels.get("DetailsChecks").toArray().map(x => JSON.parse(JSON.stringify(x)));
        cloneGridModels = cloneGridModels.set("DetailsChecks", Immutable.List(detailsCheckClones));
        store.PatientMedicationGridUndoModels[request.PatientId] = cloneGridModels;
    }

    private okSettingsToggle(request: LocalEvent, store: PatientStore): void {
        let medicationGridModel = store.PatientMedicationGridModels[request.PatientId];

        medicationGridModel = medicationGridModel.set("IsSearchSettingsOpen", false);
        store.PatientMedicationGridModels[request.PatientId] = medicationGridModel;

        this.applySortingAndOrdering(request.PatientId, store);
    }

    private applySortingAndOrdering(patientId: string, store: PatientStore): void {
        let summaryModel = store.PatientMedicalSummaryModels[patientId];
        // get the "biglist" from store
        let unfiltered = summaryModel.get("MedicalItemsMedicationAndPrescription").toArray();
        let newFilteredList = store.filterAndSortMedication(patientId, unfiltered);
        summaryModel = summaryModel.set("FilteredMedicalItemsMedicationAndPrescription", Immutable.List(newFilteredList));

        store.PatientMedicalSummaryModels[patientId] = summaryModel;
    }

    private cancelSettingsToggle(request: LocalEvent, store: PatientStore): void {
        let medicationGridModel = store.PatientMedicationGridUndoModels[request.PatientId];

        medicationGridModel = medicationGridModel.set("IsSearchSettingsOpen", false);
        store.PatientMedicationGridModels[request.PatientId] = medicationGridModel;

        medicationGridModel = medicationGridModel.set("ShowPatientMedicationDisplaySetting", false);
    }

    private columnOrderPopupClose(request: LocalEvent, store: PatientStore): void {
        let medicationGridModel = store.PatientMedicationGridModels[request.PatientId];
        store.PatientMedicationGridModels[request.PatientId] = medicationGridModel.set("IsColumnOrderingPopupOpen", false);
    }

    private columnOrderPopupOpen(request: LocalEvent, store: PatientStore): void {
        let medicationGridModel = store.PatientMedicationGridModels[request.PatientId];
        store.PatientMedicationGridModels[request.PatientId] = medicationGridModel
            .set("IsColumnOrderingPopupOpen", true)
            .set("IsSearchSettingsOpen", false);
    }

    private searchTextChanged(request: LocalEvent, store: PatientStore): void {
        let value = request.Get<string>("value");
        let medicationGridModel = store.PatientMedicationGridModels[request.PatientId];
        medicationGridModel = medicationGridModel.set("SearchText", value);

        store.PatientMedicationGridModels[request.PatientId] = medicationGridModel;
    }

    private searchDateFromChanged(request: LocalEvent, store: PatientStore): void {
        let value = request.Get<Date>("value");
        let medicationGridModel = store.PatientMedicationGridModels[request.PatientId];
        medicationGridModel = medicationGridModel.set("FromDate", value);

        store.PatientMedicationGridModels[request.PatientId] = medicationGridModel;
    }

    private searchDateToChanged(request: LocalEvent, store: PatientStore): void {
        let value = request.Get<Date>("value");
        let medicationGridModel = store.PatientMedicationGridModels[request.PatientId];
        medicationGridModel = medicationGridModel.set("ToDate", value);

        store.PatientMedicationGridModels[request.PatientId] = medicationGridModel;
    }

    private statusCheckChanged(request: LocalEvent, store: PatientStore): void {
        let target = request.Get<CheckboxTarget>("target");
        let medicationGridModel = store.PatientMedicationGridModels[request.PatientId];

        switch (target) {
            case (CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_AllCheck):

                let newVal = !medicationGridModel.get("IsAllStatusChecked");
                medicationGridModel = medicationGridModel.set("IsAllStatusChecked", newVal);
                medicationGridModel = medicationGridModel.set("IsAllStatusChecked", newVal);
                medicationGridModel = medicationGridModel.set("StatusChecks", this.helper.ChangeCheckStateFromAllICheckable(medicationGridModel.get("StatusChecks"), newVal));

                break;

            default:

                let statusChecks = medicationGridModel.get("StatusChecks").toArray();
                let checkToChangeIdx: number = statusChecks.indexOfObject("Id", target.toString());
                if (checkToChangeIdx >= 0) {
                    let checkToChange = statusChecks[checkToChangeIdx];
                    checkToChange.Checked = !checkToChange.Checked;
                    statusChecks[checkToChangeIdx] = checkToChange;
                }
                medicationGridModel = medicationGridModel.set("StatusChecks", Immutable.List(statusChecks));

                break;
        }

        store.PatientMedicationGridModels[request.PatientId] = medicationGridModel;
    }

    private statusOriginChanged(request: LocalEvent, store: PatientStore): void {
        let target = request.Get<CheckboxTarget>("target");
        var medicationGridModel = store.PatientMedicationGridModels[request.PatientId];

        switch (target) {
            case (CheckboxTarget.Myle_PatientCPP_MedicationSearch_Origin_AllCheck):
                let newVal = !medicationGridModel.get("IsAllOriginChecked");
                medicationGridModel = medicationGridModel.set("IsAllOriginChecked", newVal);
                medicationGridModel = medicationGridModel.set("IsAllOriginChecked", newVal);
                medicationGridModel = medicationGridModel.set("OriginChecks", this.helper.ChangeCheckStateFromAllICheckable(medicationGridModel.get("OriginChecks"), newVal));
                break;

            default:

                let originChecks = medicationGridModel.get("OriginChecks").toArray();
                let checkToChangeIdx: number = originChecks.indexOfObject("Id", target.toString());
                if (checkToChangeIdx >= 0) {
                    let checkToChange = originChecks[checkToChangeIdx];
                    checkToChange.Checked = !checkToChange.Checked;
                    originChecks[checkToChangeIdx] = checkToChange;
                }
                medicationGridModel = medicationGridModel.set("OriginChecks", Immutable.List(originChecks));
                break;
        }

        store.PatientMedicationGridModels[request.PatientId] = medicationGridModel;
    }

    private detailsChanged(request: LocalEvent, store: PatientStore): void {
        let target = request.Get<CheckboxTarget>("target");
        let medicationGridModel = store.PatientMedicationGridModels[request.PatientId];

        switch (target) {
            case (CheckboxTarget.Myle_PatientCPP_MedicationSearch_Details_AllCheck):
                let newVal = !medicationGridModel.get("IsAllDetailsChecked");
                medicationGridModel = medicationGridModel.set("IsAllDetailsChecked", newVal);
                medicationGridModel = medicationGridModel.set("DetailsChecks", this.helper.ChangeCheckStateFromAllICheckable(medicationGridModel.get("DetailsChecks"), newVal));
                break;

            default:
                let detailsChecks = medicationGridModel.get("DetailsChecks").toArray();
                let checkToChangeIdx: number = detailsChecks.indexOfObject("Id", target.toString());
                if (checkToChangeIdx >= 0) {
                    let checkToChange = detailsChecks[checkToChangeIdx];
                    checkToChange.Checked = !checkToChange.Checked;
                    detailsChecks[checkToChangeIdx] = checkToChange;
                }
                medicationGridModel = medicationGridModel.set("DetailsChecks", Immutable.List(detailsChecks));
                break;
        }

        store.PatientMedicationGridModels[request.PatientId] = medicationGridModel;
    }

    private setColumnSort(request: LocalEvent, store: PatientStore): void {
        const tableTarget = request.Get<MyleTableTarget>("target");

        if (tableTarget === MyleTableTarget.Myle_PatientCPP_MedicalItemMedicationSortClick) {
            let medicationGridModel = store.PatientMedicationGridModels[request.PatientId];
            const sortColumn = request.Get<MedicalSummaryMedicationDetailsSortColumn>("sortColumn");
            const column = request.Get<MyleTableColumn>("columnObject");
            let sortDirection = medicationGridModel.get("SortDirection");

            if (sortColumn != null && sortColumn != undefined) {
                const currentSortColumn = medicationGridModel.get("SortColumn");

                sortDirection = currentSortColumn == sortColumn
                    ? (sortDirection == SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC) // toggle sort direction
                    : column?.columnOptions?.SortDirection ?? SortDirection.ASC;

                medicationGridModel = medicationGridModel.set("SortDirection", sortDirection);
                medicationGridModel = medicationGridModel.set("SortColumn", sortColumn);

                this.applySortingAndOrdering(request.PatientId, store);
            }

            store.PatientMedicationGridModels[request.PatientId] = medicationGridModel;
        }
    }

    private onContainerClick(request: LocalEvent, store: PatientStore): void {
        let medicationGridModel = store.PatientMedicationGridModels[request.PatientId];

        if (medicationGridModel) {
            if (medicationGridModel.get("IsColumnOrderingPopupOpen")) {
                medicationGridModel = medicationGridModel.set("IsColumnOrderingPopupOpen", false);
            }
            if (medicationGridModel.get("IsSearchSettingsOpen") === true) {
                medicationGridModel = medicationGridModel.set("IsSearchSettingsOpen", false);

                store.PatientMedicationGridModels[request.PatientId] = medicationGridModel;

                window.setTimeout(() => {
                    LocalEventDispatcher.dispatch(new LocalEvent(LocalPatientOperations.MedicationGrid.SettingsOk));
                }, 10);
                store.EmitChangeToListeners();
            }
        }
    }

    private insertMedicationNote(request: LocalEvent, store: PatientStore): void {
        const note = request.Get<MedicationNote>("note");
        const patientContactId = request.Get<string>("patientContactId");
        const medicationId = request.Get<string>("medicationId");

        let patientMedicalSummaryModel = patientStore.PatientMedicalSummaryModels[patientContactId];

        const prescriptions = patientMedicalSummaryModel.get("MedicalItemsMedicationAndPrescription");
        const prescription = prescriptions.find(prescription => prescription.Model.Id === medicationId);

        if (!prescription) {
            console.error(`Can't insert prescription: medication ID ${medicationId} not found.`);
            return;
        }

        patientStore.PatientMedicalSummaryModels[patientContactId] = patientMedicalSummaryModel
            .set("MedicationNotes", patientMedicalSummaryModel.get("MedicationNotes").addMedicationNote(note.MedicationId, note));

        this.applySortingAndOrdering(patientContactId, store);
    }

    private updatePatientComplianceValue(request: LocalEvent): void {
        const patientContactId = request.Get<string>("patientContactId");
        const medicationId = request.Get<string>("medicationId");
        const isPatientCompliant = request.Get<boolean | null>("isPatientCompliant");

        const patientMedicalSummaryModel = patientStore.PatientMedicalSummaryModels[patientContactId];
        const medication = patientMedicalSummaryModel?.get("MedicalItemsMedicationAndPrescription")?.find(medication => medication.Model.Id === medicationId);

        if (!medication) {
            console.error(`Can't update patient compliance: medication ID ${medicationId} not found.`);
            return;
        }

        medication.Model.IsPatientCompliant = isPatientCompliant;
    }

    private loadMedicationNotes(request: LocalEvent): void {
        const notes = request.Get<MedicationNote[]>("notes");
        const medicationId = request.Get<string>("medicationId");
        const patientContactId = request.Get<string>("patientContactId");
        const patientMedicalSummaryModel = patientStore.PatientMedicalSummaryModels[patientContactId];
        patientStore.PatientMedicalSummaryModels[patientContactId] = patientMedicalSummaryModel.set("MedicationNotes", patientMedicalSummaryModel.get("MedicationNotes").loadMedicationNotes(notes, medicationId));
    }
}

export { PatientMedicationGridHandler };
